/**
 * Multi-level BOM, MRP, putaway, wave picking, SSCC and shipping documents
 * (plan 4.8–4.10).
 */
const INCOTERMS_EN = {
  EXW: "Ex Works", FCA: "Free Carrier", CPT: "Carriage Paid To", CIP: "Carriage and Insurance Paid To",
  DAP: "Delivered at Place", DPU: "Delivered at Place Unloaded", DDP: "Delivered Duty Paid",
  FAS: "Free Alongside Ship", FOB: "Free on Board", CFR: "Cost and Freight", CIF: "Cost, Insurance and Freight"
};

const en = {
  nav: { mrp: "MRP", pickWaves: "Wave Picking" },
  stock: { replenishment: { fields: { lotSize: "Lot size (multiple)" } } },
  bom: {
    title: "Bill of materials",
    component: "Component",
    selectProduct: "Select product…",
    quantity: "Qty per unit",
    scrapPct: "Scrap %",
    alternative: "Alternative material",
    altRatio: "Alt. ratio",
    alternatives: "Alternatives",
    phantom: "Phantom (not stocked)",
    subAssembly: "Sub-recipe",
    addLine: "Add line",
    remove: "Remove",
    changeNote: "Change note",
    saveRevision: "Save as new revision",
    saved: "Revisions",
    none: "No BOM yet.",
    revision: "rev",
    activate: "Activate",
    copyRevision: "Copy to new revision",
    compare: "Compare with previous",
    noChanges: "No line changes.",
    explosion: "BOM explosion",
    explode: "Explode",
    grossQty: "Gross qty",
    requirements: "Purchased material totals",
    engineeringRevisions: "Engineering revisions",
    revTitle: "Title",
    optional: "Optional",
    createRevision: "Create revision",
    noEngineeringRevisions: "No engineering revisions.",
    statuses: { draft: "Draft", active: "Active", superseded: "Superseded" },
    changes: { added: "Added", removed: "Removed", changed: "Changed" },
    fields: { quantity: "quantity", unit: "unit", scrapPct: "scrap", issueMethod: "issue method", subBomId: "sub-recipe", phantom: "phantom", alternatives: "alternatives" },
    issueMethods: { manual: "Manual issue", backflush: "Backflush" },
    errors: {
      PRODUCT_REQUIRED: "Product is required.",
      LINES_REQUIRED: "Add at least one line.",
      COMPONENT_REQUIRED: "Every line needs a component.",
      SELF_REFERENCE: "A product cannot be its own component.",
      DUPLICATE_COMPONENT: "A component appears twice.",
      QTY_INVALID: "Quantity must be greater than zero.",
      SCRAP_INVALID: "Scrap rate must be between 0 and 99.",
      ALTERNATIVE_INVALID: "Alternative must be another product with a ratio above zero.",
      CYCLE: "The sub-recipes lead back to this product (cycle).",
      TOO_DEEP: "The recipe has too many levels.",
      NOT_FOUND: "No BOM for this product."
    }
  },
  mrp: {
    title: "Material requirements planning",
    subtitle: "Net requirements from sales forecasts and open orders, with make / buy proposals",
    procurement: "Procurement",
    procurementAuto: "Automatic (BOM → make)",
    actions: { make: "Make", buy: "Buy" },
    finishedGoods: "finished goods",
    issued: "Material issued.",
    scrapRecorded: "Scrap recorded.",
    issueMethod: "Issue method",
    scrapped: "Scrapped",
    consumeHint: "“Consume materials” issues the manual lines; backflush lines are issued automatically when output is posted.",
    materialMove: "Material issue / scrap",
    moveMode: "Movement",
    manualIssue: "Manual issue",
    recordScrap: "Record scrap",
    material: "Material",
    scrapReason: "Reason",
    outputScrap: "Scrapped in production (not stocked)",
    forecasts: "Sales forecasts",
    product: "Product",
    period: "Month",
    forecastQty: "Forecast qty",
    noForecasts: "No forecasts yet.",
    run: "MRP run",
    horizon: "Horizon",
    months: "{{n}} months",
    policy: "Demand",
    policies: { max: "Max(forecast, orders)", sum: "Forecast + orders", orders: "Orders only" },
    runButton: "Run MRP",
    runHint: "Run MRP to see net requirements per item.",
    llc: "LLC",
    llcHint: "Low-level code: the deepest BOM level of the item",
    gross: "Gross",
    onHand: "On hand",
    receipts: "Receipts",
    safety: "Safety",
    net: "Net",
    planned: "Planned",
    action: "Proposal",
    needDate: "Need date",
    releaseDate: "Release by",
    independent: "Independent",
    dependent: "Dependent",
    late: "late",
    createProposals: "Create selected proposals",
    nothingToPlan: "Stock and open supply cover the demand.",
    created: "{{n}} documents created:",
    errors: {
      ALTERNATIVE_NOT_ALLOWED: "This material is not an alternative of the line.",
      LINE_NOT_FOUND: "The material is not on this order.",
      QTY_REQUIRED: "Enter a quantity.",
      SCRAP_EXCEEDS_OUTPUT: "Finished-goods scrap cannot exceed the posted output.",
      MO_NOT_EXECUTABLE: "The order is not released.",
      FORECAST_PRODUCT_REQUIRED: "Select a product.",
      FORECAST_PERIOD_INVALID: "Select a month.",
      FORECAST_QTY_INVALID: "Quantity must be zero or more.",
      MRP_POLICY_INVALID: "Unknown demand policy.",
      MRP_BOM_CYCLE: "A BOM cycle stops the run; fix the recipes first."
    }
  },
  putaway: {
    label: "Putaway",
    headerLocation: "Location above",
    apply: "Apply putaway suggestions",
    noFit: "no location fits",
    rules: { quarantine: "quarantine", fixed: "fixed location", consolidate: "same product", class: "storage class", empty: "empty location" },
    pickSequence: "Route order",
    storageClass: "Storage class",
    storageClassHint: "e.g. cold, bulk",
    fixedLocation: "Fixed location"
  },
  wave: {
    title: "Wave picking",
    subtitle: "One pick round for several reserved orders, ordered by location route",
    reservedOrders: "Reserved orders",
    create: "Create wave",
    noOrders: "No reserved orders waiting.",
    waves: "Waves",
    none: "No waves yet.",
    statuses: { open: "Open", completed: "Completed", cancelled: "Cancelled" },
    print: "Print pick list",
    complete: "Complete wave",
    step: "Step",
    location: "Location",
    orders: "Orders",
    picked: "Picked",
    pickList: "Pick list",
    shortages: "Not enough stock in locations:",
    selectHint: "Select or create a wave.",
    errors: {
      WAVE_ORDERS_REQUIRED: "Select at least one order.",
      WAVE_ORDER_NOT_RESERVED: "Only reserved orders can join a wave.",
      WAVE_MIXED_WAREHOUSES: "All orders of a wave must ship from the same warehouse.",
      WAVE_CLOSED: "This wave is closed.",
      WAVE_TASKS_OPEN: "Mark every task as picked first."
    }
  },
  shipDoc: {
    title: "Shipping documents",
    docType: "Document",
    countryFormat: "Country format",
    incotermPlace: "Named place",
    address: "Address",
    taxNumber: "Tax / VAT number",
    packagesFound: "{{n}} packages linked to the order",
    printHint: "The print dialog saves the document as PDF.",
    printLabels: "SSCC labels",
    print: "Print / PDF",
    requestedDate: "Requested date",
    salesOrder: "Sales order",
    ssccLabel: "SSCC label",
    gs1Title: "GS1 / SSCC",
    gs1Prefix: "GS1 company prefix (7–10 digits)",
    gs1Extension: "Extension digit",
    gs1Hint: "Packages and pallets get an SSCC with check digit from this prefix.",
    titles: {
      deliveryNote: "Delivery note",
      deliveryNoteTR: "Dispatch note (İrsaliye)",
      deliveryNoteDE: "Delivery note (Lieferschein)",
      commercialInvoice: "Commercial invoice",
      packingList: "Packing list"
    },
    labels: {
      no: "#", sku: "SKU", name: "Description", hsCode: "HS code", origin: "Origin", quantity: "Quantity", unit: "Unit",
      unitPrice: "Unit price", amount: "Amount", lotNumber: "Lot", netWeightKg: "Net kg", grossWeightKg: "Gross kg",
      packages: "Packages", code: "Code", sscc: "SSCC", type: "Type", contents: "Contents", dimensions: "Dimensions",
      number: "Document no.", issueDate: "Issue date", shipDate: "Ship date", shipTime: "Ship time", orderCode: "Order",
      incoterm: "Incoterm", carrier: "Carrier", vehiclePlate: "Vehicle", trackingNumber: "Tracking no.", currency: "Currency",
      totalQuantity: "Total quantity", totalAmount: "Total amount", packageCount: "Packages", seller: "Seller / shipper",
      buyer: "Buyer / consignee", shipTo: "Ship to", taxOffice: "Tax office", delivererSign: "Delivered by (signature)",
      receiverSign: "Received by (signature)", declaration: "We declare that the information on this invoice is true and correct.",
      shipFrom: "Ship from", count: "Count", reference: "Reference"
    },
    incoterms: INCOTERMS_EN,
    fields: {
      company_taxNumber: "seller tax number", company_taxOffice: "seller tax office", customer_taxNumber: "buyer tax number",
      company_vatId: "seller VAT number", incoterm: "Incoterm"
    },
    errors: {
      DOC_TYPE_INVALID: "Unknown document type.",
      DOC_FIELDS_MISSING: "Missing for this format:",
      INCOTERM_INVALID: "Unknown Incoterm.",
      INCOTERM_PLACE_REQUIRED: "Incoterms need a named place.",
      SSCC_PREFIX_MISSING: "Set the GS1 company prefix in inventory policies first.",
      POLICY_GS1_PREFIX: "GS1 company prefix must be 7–10 digits.",
      POLICY_GS1_EXTENSION: "Extension digit must be 0–9."
    }
  }
};

const tr = {
  nav: { mrp: "MRP", pickWaves: "Dalga Toplama" },
  stock: { replenishment: { fields: { lotSize: "Parti büyüklüğü (kat)" } } },
  bom: {
    title: "Ürün reçetesi (BOM)",
    component: "Bileşen",
    selectProduct: "Ürün seçin…",
    quantity: "Birim başına miktar",
    scrapPct: "Fire %",
    alternative: "Alternatif malzeme",
    altRatio: "Alt. oran",
    alternatives: "Alternatifler",
    phantom: "Fantom (stoklanmaz)",
    subAssembly: "Alt reçete",
    addLine: "Satır ekle",
    remove: "Kaldır",
    changeNote: "Değişiklik notu",
    saveRevision: "Yeni revizyon olarak kaydet",
    saved: "Revizyonlar",
    none: "Henüz reçete yok.",
    revision: "rev",
    activate: "Aktifleştir",
    copyRevision: "Yeni revizyona kopyala",
    compare: "Öncekiyle karşılaştır",
    noChanges: "Satır değişikliği yok.",
    explosion: "Reçete patlatma",
    explode: "Patlat",
    grossQty: "Brüt miktar",
    requirements: "Satın alınan malzeme toplamları",
    engineeringRevisions: "Mühendislik revizyonları",
    revTitle: "Başlık",
    optional: "İsteğe bağlı",
    createRevision: "Revizyon oluştur",
    noEngineeringRevisions: "Mühendislik revizyonu yok.",
    statuses: { draft: "Taslak", active: "Aktif", superseded: "Geçersiz" },
    changes: { added: "Eklendi", removed: "Çıkarıldı", changed: "Değişti" },
    fields: { quantity: "miktar", unit: "birim", scrapPct: "fire", issueMethod: "çekme yöntemi", subBomId: "alt reçete", phantom: "fantom", alternatives: "alternatifler" },
    issueMethods: { manual: "Manuel çekme", backflush: "Otomatik geri tüketim (backflush)" },
    errors: {
      PRODUCT_REQUIRED: "Ürün zorunludur.",
      LINES_REQUIRED: "En az bir satır ekleyin.",
      COMPONENT_REQUIRED: "Her satırda bileşen olmalı.",
      SELF_REFERENCE: "Ürün kendi bileşeni olamaz.",
      DUPLICATE_COMPONENT: "Bir bileşen iki kez girilmiş.",
      QTY_INVALID: "Miktar sıfırdan büyük olmalı.",
      SCRAP_INVALID: "Fire oranı 0 ile 99 arasında olmalı.",
      ALTERNATIVE_INVALID: "Alternatif başka bir ürün olmalı ve oranı sıfırdan büyük olmalı.",
      CYCLE: "Alt reçeteler bu ürüne geri dönüyor (döngü).",
      TOO_DEEP: "Reçetede çok fazla seviye var.",
      NOT_FOUND: "Bu ürün için reçete yok."
    }
  },
  mrp: {
    title: "Malzeme ihtiyaç planlaması",
    subtitle: "Satış tahmini ve açık siparişlerden net ihtiyaç; üretim / satın alma önerileri",
    procurement: "Tedarik türü",
    procurementAuto: "Otomatik (reçete varsa üret)",
    actions: { make: "Üret", buy: "Satın al" },
    finishedGoods: "mamul",
    issued: "Malzeme çekildi.",
    scrapRecorded: "Fire kaydedildi.",
    issueMethod: "Çekme yöntemi",
    scrapped: "Fire",
    consumeHint: "“Malzeme tüket” manuel satırları çeker; backflush satırları üretim girişi yapılınca otomatik düşülür.",
    materialMove: "Malzeme çekme / fire",
    moveMode: "Hareket",
    manualIssue: "Manuel çekme",
    recordScrap: "Fire kaydet",
    material: "Malzeme",
    scrapReason: "Neden",
    outputScrap: "Üretimde fire (stoğa girmez)",
    forecasts: "Satış tahminleri",
    product: "Ürün",
    period: "Ay",
    forecastQty: "Tahmin miktarı",
    noForecasts: "Henüz tahmin yok.",
    run: "MRP çalıştırma",
    horizon: "Ufuk",
    months: "{{n}} ay",
    policy: "Talep",
    policies: { max: "Maks(tahmin, sipariş)", sum: "Tahmin + sipariş", orders: "Yalnız siparişler" },
    runButton: "MRP çalıştır",
    runHint: "Kalem bazında net ihtiyaçları görmek için MRP'yi çalıştırın.",
    llc: "DSK",
    llcHint: "Düşük seviye kodu: kalemin en derin reçete seviyesi",
    gross: "Brüt",
    onHand: "Eldeki",
    receipts: "Planlı giriş",
    safety: "Emniyet",
    net: "Net",
    planned: "Planlanan",
    action: "Öneri",
    needDate: "İhtiyaç tarihi",
    releaseDate: "Başlatma",
    independent: "Bağımsız",
    dependent: "Bağımlı",
    late: "gecikmiş",
    createProposals: "Seçili önerileri oluştur",
    nothingToPlan: "Stok ve açık tedarik talebi karşılıyor.",
    created: "{{n}} belge oluşturuldu:",
    errors: {
      ALTERNATIVE_NOT_ALLOWED: "Bu malzeme satırın alternatifi değil.",
      LINE_NOT_FOUND: "Malzeme bu iş emrinde yok.",
      QTY_REQUIRED: "Miktar girin.",
      SCRAP_EXCEEDS_OUTPUT: "Mamul firesi girilen üretimi aşamaz.",
      MO_NOT_EXECUTABLE: "İş emri serbest bırakılmamış.",
      FORECAST_PRODUCT_REQUIRED: "Ürün seçin.",
      FORECAST_PERIOD_INVALID: "Ay seçin.",
      FORECAST_QTY_INVALID: "Miktar sıfır veya daha büyük olmalı.",
      MRP_POLICY_INVALID: "Bilinmeyen talep politikası.",
      MRP_BOM_CYCLE: "Reçete döngüsü çalıştırmayı durdurdu; önce reçeteleri düzeltin."
    }
  },
  putaway: {
    label: "Yerleştirme",
    headerLocation: "Yukarıdaki lokasyon",
    apply: "Yerleştirme önerilerini uygula",
    noFit: "uygun lokasyon yok",
    rules: { quarantine: "karantina", fixed: "sabit lokasyon", consolidate: "aynı ürün", class: "depolama sınıfı", empty: "boş lokasyon" },
    pickSequence: "Rota sırası",
    storageClass: "Depolama sınıfı",
    storageClassHint: "örn. soğuk, dökme",
    fixedLocation: "Sabit lokasyon"
  },
  wave: {
    title: "Dalga toplama",
    subtitle: "Birden çok rezerve siparişi lokasyon rotasına göre tek turda toplayın",
    reservedOrders: "Rezerve siparişler",
    create: "Dalga oluştur",
    noOrders: "Bekleyen rezerve sipariş yok.",
    waves: "Dalgalar",
    none: "Henüz dalga yok.",
    statuses: { open: "Açık", completed: "Tamamlandı", cancelled: "İptal" },
    print: "Toplama listesini yazdır",
    complete: "Dalgayı tamamla",
    step: "Adım",
    location: "Lokasyon",
    orders: "Siparişler",
    picked: "Toplandı",
    pickList: "Toplama listesi",
    shortages: "Lokasyonlarda yeterli stok yok:",
    selectHint: "Bir dalga seçin veya oluşturun.",
    errors: {
      WAVE_ORDERS_REQUIRED: "En az bir sipariş seçin.",
      WAVE_ORDER_NOT_RESERVED: "Dalgaya yalnız rezerve siparişler girebilir.",
      WAVE_MIXED_WAREHOUSES: "Dalgadaki siparişler aynı depodan çıkmalı.",
      WAVE_CLOSED: "Bu dalga kapalı.",
      WAVE_TASKS_OPEN: "Önce tüm görevleri toplandı olarak işaretleyin."
    }
  },
  shipDoc: {
    title: "Sevk belgeleri",
    docType: "Belge",
    countryFormat: "Ülke formatı",
    incotermPlace: "Belirtilen yer",
    address: "Adres",
    taxNumber: "Vergi / KDV no",
    packagesFound: "Siparişe bağlı {{n}} koli",
    printHint: "Yazdırma penceresi belgeyi PDF olarak kaydeder.",
    printLabels: "SSCC etiketleri",
    print: "Yazdır / PDF",
    requestedDate: "İstenen tarih",
    salesOrder: "Satış siparişi",
    ssccLabel: "SSCC etiketi",
    gs1Title: "GS1 / SSCC",
    gs1Prefix: "GS1 firma öneki (7–10 hane)",
    gs1Extension: "Uzantı hanesi",
    gs1Hint: "Koli ve paletler bu önekten kontrol haneli SSCC alır.",
    titles: {
      deliveryNote: "Teslimat notu",
      deliveryNoteTR: "Sevk irsaliyesi",
      deliveryNoteDE: "Teslimat notu (Lieferschein)",
      commercialInvoice: "Ticari fatura",
      packingList: "Çeki listesi"
    },
    labels: {
      no: "#", sku: "Stok kodu", name: "Açıklama", hsCode: "GTİP", origin: "Menşe", quantity: "Miktar", unit: "Birim",
      unitPrice: "Birim fiyat", amount: "Tutar", lotNumber: "Lot", netWeightKg: "Net kg", grossWeightKg: "Brüt kg",
      packages: "Koliler", code: "Kod", sscc: "SSCC", type: "Tür", contents: "İçerik", dimensions: "Ölçüler",
      number: "Belge no", issueDate: "Düzenleme tarihi", shipDate: "Sevk tarihi", shipTime: "Sevk saati", orderCode: "Sipariş",
      incoterm: "Teslim şekli", carrier: "Taşıyıcı", vehiclePlate: "Araç plakası", trackingNumber: "Takip no", currency: "Para birimi",
      totalQuantity: "Toplam miktar", totalAmount: "Toplam tutar", packageCount: "Koli adedi", seller: "Satıcı / gönderen",
      buyer: "Alıcı", shipTo: "Teslim adresi", taxOffice: "Vergi dairesi", delivererSign: "Teslim eden (imza)",
      receiverSign: "Teslim alan (imza)", declaration: "Bu faturadaki bilgilerin doğru olduğunu beyan ederiz.",
      shipFrom: "Gönderen", count: "Adet", reference: "Referans"
    },
    incoterms: {
      EXW: "İşyerinde teslim", FCA: "Taşıyıcıya teslim", CPT: "Taşıma ücreti ödenmiş", CIP: "Taşıma ve sigorta ödenmiş",
      DAP: "Belirtilen yerde teslim", DPU: "Boşaltılmış olarak teslim", DDP: "Gümrük vergisi ödenmiş teslim",
      FAS: "Gemi doğrultusunda teslim", FOB: "Gemide teslim", CFR: "Masraflar ve navlun", CIF: "Masraflar, sigorta ve navlun"
    },
    fields: {
      company_taxNumber: "satıcı vergi no", company_taxOffice: "satıcı vergi dairesi", customer_taxNumber: "alıcı vergi no",
      company_vatId: "satıcı KDV no", incoterm: "teslim şekli"
    },
    errors: {
      DOC_TYPE_INVALID: "Bilinmeyen belge türü.",
      DOC_FIELDS_MISSING: "Bu format için eksik:",
      INCOTERM_INVALID: "Bilinmeyen teslim şekli.",
      INCOTERM_PLACE_REQUIRED: "Incoterms için yer belirtilmeli.",
      SSCC_PREFIX_MISSING: "Önce stok politikalarında GS1 firma önekini girin.",
      POLICY_GS1_PREFIX: "GS1 firma öneki 7–10 hane olmalı.",
      POLICY_GS1_EXTENSION: "Uzantı hanesi 0–9 olmalı."
    }
  }
};

const ar = {
  nav: { mrp: "تخطيط المواد", pickWaves: "الالتقاط بالموجات" },
  stock: { replenishment: { fields: { lotSize: "حجم الدفعة (مضاعف)" } } },
  bom: {
    title: "قائمة المواد",
    component: "المكوّن",
    selectProduct: "اختر منتجًا…",
    quantity: "الكمية لكل وحدة",
    scrapPct: "الهالك %",
    alternative: "مادة بديلة",
    altRatio: "نسبة البديل",
    alternatives: "البدائل",
    phantom: "وهمي (غير مخزّن)",
    subAssembly: "وصفة فرعية",
    addLine: "إضافة سطر",
    remove: "إزالة",
    changeNote: "ملاحظة التغيير",
    saveRevision: "حفظ كمراجعة جديدة",
    saved: "المراجعات",
    none: "لا توجد قائمة مواد بعد.",
    revision: "مراجعة",
    activate: "تفعيل",
    copyRevision: "نسخ إلى مراجعة جديدة",
    compare: "مقارنة بالسابقة",
    noChanges: "لا تغييرات في الأسطر.",
    explosion: "تفجير قائمة المواد",
    explode: "تفجير",
    grossQty: "الكمية الإجمالية",
    requirements: "إجمالي المواد المشتراة",
    engineeringRevisions: "المراجعات الهندسية",
    revTitle: "العنوان",
    optional: "اختياري",
    createRevision: "إنشاء مراجعة",
    noEngineeringRevisions: "لا توجد مراجعات هندسية.",
    statuses: { draft: "مسودة", active: "نشطة", superseded: "مستبدلة" },
    changes: { added: "مضاف", removed: "محذوف", changed: "معدّل" },
    fields: { quantity: "الكمية", unit: "الوحدة", scrapPct: "الهالك", issueMethod: "طريقة الصرف", subBomId: "الوصفة الفرعية", phantom: "وهمي", alternatives: "البدائل" },
    issueMethods: { manual: "صرف يدوي", backflush: "صرف تلقائي (Backflush)" },
    errors: {
      PRODUCT_REQUIRED: "المنتج مطلوب.",
      LINES_REQUIRED: "أضف سطرًا واحدًا على الأقل.",
      COMPONENT_REQUIRED: "كل سطر يحتاج إلى مكوّن.",
      SELF_REFERENCE: "لا يمكن أن يكون المنتج مكوّنًا لنفسه.",
      DUPLICATE_COMPONENT: "المكوّن مكرر.",
      QTY_INVALID: "يجب أن تكون الكمية أكبر من صفر.",
      SCRAP_INVALID: "يجب أن تكون نسبة الهالك بين 0 و99.",
      ALTERNATIVE_INVALID: "يجب أن يكون البديل منتجًا آخر بنسبة أكبر من صفر.",
      CYCLE: "الوصفات الفرعية تعود إلى هذا المنتج (حلقة).",
      TOO_DEEP: "الوصفة تحتوي على مستويات كثيرة جدًا.",
      NOT_FOUND: "لا توجد قائمة مواد لهذا المنتج."
    }
  },
  mrp: {
    title: "تخطيط متطلبات المواد",
    subtitle: "صافي الاحتياجات من التوقعات والطلبات المفتوحة مع مقترحات التصنيع والشراء",
    procurement: "نوع التوريد",
    procurementAuto: "تلقائي (قائمة مواد ← تصنيع)",
    actions: { make: "تصنيع", buy: "شراء" },
    finishedGoods: "منتج تام",
    issued: "تم صرف المادة.",
    scrapRecorded: "تم تسجيل الهالك.",
    issueMethod: "طريقة الصرف",
    scrapped: "الهالك",
    consumeHint: "«استهلاك المواد» يصرف الأسطر اليدوية؛ أسطر الصرف التلقائي تُصرف عند تسجيل الإنتاج.",
    materialMove: "صرف المواد / الهالك",
    moveMode: "الحركة",
    manualIssue: "صرف يدوي",
    recordScrap: "تسجيل هالك",
    material: "المادة",
    scrapReason: "السبب",
    outputScrap: "هالك الإنتاج (لا يدخل المخزون)",
    forecasts: "توقعات المبيعات",
    product: "المنتج",
    period: "الشهر",
    forecastQty: "الكمية المتوقعة",
    noForecasts: "لا توجد توقعات بعد.",
    run: "تشغيل MRP",
    horizon: "الأفق",
    months: "{{n}} أشهر",
    policy: "الطلب",
    policies: { max: "الأكبر(التوقع، الطلبات)", sum: "التوقع + الطلبات", orders: "الطلبات فقط" },
    runButton: "تشغيل MRP",
    runHint: "شغّل MRP لعرض صافي الاحتياجات لكل صنف.",
    llc: "المستوى",
    llcHint: "رمز المستوى الأدنى: أعمق مستوى للصنف في قوائم المواد",
    gross: "الإجمالي",
    onHand: "المتوفر",
    receipts: "الواردات المخططة",
    safety: "الأمان",
    net: "الصافي",
    planned: "المخطط",
    action: "المقترح",
    needDate: "تاريخ الحاجة",
    releaseDate: "تاريخ الإطلاق",
    independent: "مستقل",
    dependent: "تابع",
    late: "متأخر",
    createProposals: "إنشاء المقترحات المحددة",
    nothingToPlan: "المخزون والتوريد المفتوح يغطيان الطلب.",
    created: "تم إنشاء {{n}} مستندات:",
    errors: {
      ALTERNATIVE_NOT_ALLOWED: "هذه المادة ليست بديلًا لهذا السطر.",
      LINE_NOT_FOUND: "المادة غير موجودة في هذا الأمر.",
      QTY_REQUIRED: "أدخل كمية.",
      SCRAP_EXCEEDS_OUTPUT: "لا يمكن أن يتجاوز هالك المنتج التام الإنتاج المسجّل.",
      MO_NOT_EXECUTABLE: "أمر التصنيع غير مطلق.",
      FORECAST_PRODUCT_REQUIRED: "اختر منتجًا.",
      FORECAST_PERIOD_INVALID: "اختر شهرًا.",
      FORECAST_QTY_INVALID: "يجب أن تكون الكمية صفرًا أو أكثر.",
      MRP_POLICY_INVALID: "سياسة طلب غير معروفة.",
      MRP_BOM_CYCLE: "حلقة في قوائم المواد أوقفت التشغيل؛ أصلح الوصفات أولًا."
    }
  },
  putaway: {
    label: "التخزين",
    headerLocation: "الموقع أعلاه",
    apply: "تطبيق مقترحات التخزين",
    noFit: "لا يوجد موقع مناسب",
    rules: { quarantine: "الحجر", fixed: "موقع ثابت", consolidate: "نفس المنتج", class: "فئة التخزين", empty: "موقع فارغ" },
    pickSequence: "ترتيب المسار",
    storageClass: "فئة التخزين",
    storageClassHint: "مثل: مبرد، سائب",
    fixedLocation: "موقع ثابت"
  },
  wave: {
    title: "الالتقاط بالموجات",
    subtitle: "جولة التقاط واحدة لعدة طلبات محجوزة مرتبة حسب مسار المواقع",
    reservedOrders: "الطلبات المحجوزة",
    create: "إنشاء موجة",
    noOrders: "لا توجد طلبات محجوزة بانتظار الالتقاط.",
    waves: "الموجات",
    none: "لا توجد موجات بعد.",
    statuses: { open: "مفتوحة", completed: "مكتملة", cancelled: "ملغاة" },
    print: "طباعة قائمة الالتقاط",
    complete: "إكمال الموجة",
    step: "الخطوة",
    location: "الموقع",
    orders: "الطلبات",
    picked: "تم الالتقاط",
    pickList: "قائمة الالتقاط",
    shortages: "المخزون غير كافٍ في المواقع:",
    selectHint: "اختر موجة أو أنشئ واحدة.",
    errors: {
      WAVE_ORDERS_REQUIRED: "اختر طلبًا واحدًا على الأقل.",
      WAVE_ORDER_NOT_RESERVED: "يمكن إضافة الطلبات المحجوزة فقط إلى الموجة.",
      WAVE_MIXED_WAREHOUSES: "يجب أن تُشحن طلبات الموجة من المستودع نفسه.",
      WAVE_CLOSED: "هذه الموجة مغلقة.",
      WAVE_TASKS_OPEN: "علّم جميع المهام كملتقطة أولًا."
    }
  },
  shipDoc: {
    title: "مستندات الشحن",
    docType: "المستند",
    countryFormat: "تنسيق الدولة",
    incotermPlace: "المكان المحدد",
    address: "العنوان",
    taxNumber: "الرقم الضريبي",
    packagesFound: "{{n}} طرود مرتبطة بالطلب",
    printHint: "نافذة الطباعة تحفظ المستند بصيغة PDF.",
    printLabels: "ملصقات SSCC",
    print: "طباعة / PDF",
    requestedDate: "التاريخ المطلوب",
    salesOrder: "أمر البيع",
    ssccLabel: "ملصق SSCC",
    gs1Title: "GS1 / SSCC",
    gs1Prefix: "بادئة شركة GS1 (7–10 أرقام)",
    gs1Extension: "رقم الامتداد",
    gs1Hint: "تحصل الطرود والمنصات على رقم SSCC مع رقم التحقق من هذه البادئة.",
    titles: {
      deliveryNote: "إشعار تسليم",
      deliveryNoteTR: "إذن شحن (İrsaliye)",
      deliveryNoteDE: "إشعار تسليم (Lieferschein)",
      commercialInvoice: "فاتورة تجارية",
      packingList: "قائمة التعبئة"
    },
    labels: {
      no: "#", sku: "رمز الصنف", name: "الوصف", hsCode: "رمز النظام المنسق", origin: "المنشأ", quantity: "الكمية", unit: "الوحدة",
      unitPrice: "سعر الوحدة", amount: "المبلغ", lotNumber: "الدفعة", netWeightKg: "الوزن الصافي كجم", grossWeightKg: "الوزن القائم كجم",
      packages: "الطرود", code: "الرمز", sscc: "SSCC", type: "النوع", contents: "المحتويات", dimensions: "الأبعاد",
      number: "رقم المستند", issueDate: "تاريخ الإصدار", shipDate: "تاريخ الشحن", shipTime: "وقت الشحن", orderCode: "الطلب",
      incoterm: "شروط التسليم", carrier: "الناقل", vehiclePlate: "المركبة", trackingNumber: "رقم التتبع", currency: "العملة",
      totalQuantity: "إجمالي الكمية", totalAmount: "إجمالي المبلغ", packageCount: "عدد الطرود", seller: "البائع / الشاحن",
      buyer: "المشتري / المرسل إليه", shipTo: "عنوان التسليم", taxOffice: "مكتب الضرائب", delivererSign: "المسلِّم (التوقيع)",
      receiverSign: "المستلِم (التوقيع)", declaration: "نقر بأن المعلومات الواردة في هذه الفاتورة صحيحة.",
      shipFrom: "من", count: "العدد", reference: "المرجع"
    },
    incoterms: {
      EXW: "تسليم أرض المصنع", FCA: "التسليم للناقل", CPT: "أجرة النقل مدفوعة", CIP: "أجرة النقل والتأمين مدفوعة",
      DAP: "التسليم في المكان", DPU: "التسليم في المكان مفرغًا", DDP: "التسليم مع دفع الرسوم",
      FAS: "التسليم بجانب السفينة", FOB: "التسليم على ظهر السفينة", CFR: "التكلفة والشحن", CIF: "التكلفة والتأمين والشحن"
    },
    fields: {
      company_taxNumber: "الرقم الضريبي للبائع", company_taxOffice: "مكتب ضرائب البائع", customer_taxNumber: "الرقم الضريبي للمشتري",
      company_vatId: "رقم ضريبة القيمة المضافة للبائع", incoterm: "شروط التسليم"
    },
    errors: {
      DOC_TYPE_INVALID: "نوع مستند غير معروف.",
      DOC_FIELDS_MISSING: "حقول ناقصة لهذا التنسيق:",
      INCOTERM_INVALID: "شرط تسليم غير معروف.",
      INCOTERM_PLACE_REQUIRED: "شروط Incoterms تتطلب مكانًا محددًا.",
      SSCC_PREFIX_MISSING: "أدخل بادئة شركة GS1 في سياسات المخزون أولًا.",
      POLICY_GS1_PREFIX: "يجب أن تتكون بادئة GS1 من 7 إلى 10 أرقام.",
      POLICY_GS1_EXTENSION: "يجب أن يكون رقم الامتداد من 0 إلى 9."
    }
  }
};

export const planningLocales = { en, tr, ar };
